import React, { useState } from 'react'
import useTranslation from 'next-translate/useTranslation'
import noop from 'lodash/noop'
import { useRouter } from 'next/router'
import InfoIcon from '@ancon/wildcat-ui/shared/icons/info.svg'

import useAppDispatch from '../../../store/hooks/useAppDispatch'
import {
  preOrderClearCheckout,
  setPreOrderHostInfoModalVisible,
  setPreOrderLeaveModalVisible,
} from '../store/preOrderSlice'
import useAppSelector from '../../../store/hooks/useAppSelector'
import {
  preOrderCheckoutIdSelector,
  preOrderCheckoutOutletIdSelector,
  preOrderCheckoutSelector,
  preOrderCheckoutTotalItemCountSelector,
  preOrderCheckoutUserItemsSelector,
  preOrderConfirmPendingSelector,
  preOrderCurrentCheckoutPendingSelector,
  preOrderUserIsAttendeeSelector,
  preOrderUserIsHostSelector,
} from '../store/preOrderSelectors'
import Button from '../../app/components/Button'
import CartHeader from '../../checkout/components/shoppingCart/CartHeader'
import CartItemList from '../../checkout/components/shoppingCart/CartItemList'
import CartFooter from '../../checkout/components/shoppingCart/CartFooter'
import CartOutOfStockPopup from '../../checkout/components/shoppingCart/CartOutOfStockPopup'
import { updateCheckout } from '../../checkout/store/checkoutThunks'
import { AppRoutes } from '../../app/constants'
import { appSetIsCartDrawerVisible } from '../../app/store/appSlice'
import { confirmPreOrderCheckout } from '../store/preOrderThunks'
import localStorageUtils from '../../app/utils/localStorageUtils'
import useAppStore from '../../../store/hooks/useAppStore'

import PreOrderTotal from './PreOrderTotal'
import LeavePreOrderModal from './LeavePreOrderModal'
import PreOrderHostInfoModal from './PreOrderHostInfoModal'
import styles from './PreOrderShoppingCart.module.scss'

export default function PreOrderShoppingCart() {
  const { t } = useTranslation('common')
  const dispatch = useAppDispatch()
  const router = useRouter()
  const store = useAppStore()

  const preOrderCheckout = useAppSelector(preOrderCheckoutSelector)
  const preOrderItems = useAppSelector(preOrderCheckoutUserItemsSelector)
  const preOrderItemsTotalQty = useAppSelector(
    preOrderCheckoutTotalItemCountSelector,
  )
  const preOrderOutletId = useAppSelector(preOrderCheckoutOutletIdSelector)
  const checkoutPending = useAppSelector(preOrderCurrentCheckoutPendingSelector)
  const isConfirmPending = useAppSelector(preOrderConfirmPendingSelector)
  const isHost = useAppSelector(preOrderUserIsHostSelector)
  const isAttendee = useAppSelector(preOrderUserIsAttendeeSelector)

  const [checkingOutOfStockItems, setCheckingOutOfStockItems] =
    useState<boolean>(false)

  const { preOrderName, orderFormat, outlet, serviceTime } = preOrderCheckout!

  function handleShowPreOrderHostInfo() {
    dispatch(setPreOrderHostInfoModalVisible(true))
  }

  function handleLeavePreOrder() {
    dispatch(setPreOrderLeaveModalVisible(true))
  }

  async function requestCheckOutOfStockItems(callback: () => void) {
    setCheckingOutOfStockItems(true)
    try {
      await dispatch(updateCheckout({})).unwrap()
      callback()
    } catch (err) {
      noop()
    } finally {
      setCheckingOutOfStockItems(false)
    }
  }

  function handleManagePreOrder() {
    router.push(AppRoutes.PreOrderManage)
    dispatch(appSetIsCartDrawerVisible(false))
  }

  async function handleConfirmPreOrder() {
    const preOrderId = preOrderCheckoutIdSelector(store.getState())

    await dispatch(confirmPreOrderCheckout())

    // Attendee no need the cart anymore
    if (isAttendee) {
      dispatch(preOrderClearCheckout())
      localStorageUtils.removeItem('cartMeta')
    }

    // Navigate to status page
    router.push(`${AppRoutes.PreOrderStatus}/${preOrderOutletId}/${preOrderId}`)
  }

  function handleOnManagePreOrderClick() {
    requestCheckOutOfStockItems(handleManagePreOrder)
  }

  function handleOnConfirmPreOrderClick() {
    requestCheckOutOfStockItems(handleConfirmPreOrder)
  }

  function handleContinueOutOfStockPopup() {
    if (isHost) {
      handleManagePreOrder()
    } else {
      handleConfirmPreOrder()
    }
  }

  return (
    <div className={styles.container}>
      <CartHeader
        title={preOrderName}
        outletName={outlet.name}
        orderFormat={orderFormat}
        serviceTime={serviceTime}
        loading={checkoutPending}
        isPreOrder
        additionalActions={
          isAttendee && (
            <Button
              variant="secondary"
              onClick={handleShowPreOrderHostInfo}
              className={styles.preOrderHostInfoButton}
              outlined
            >
              <InfoIcon />
              <span>{t('preOrder.hostInfo')}</span>
            </Button>
          )
        }
      />
      <CartItemList
        items={preOrderItems}
        canEditItems
        itemDisabled={checkoutPending}
      />
      <CartFooter
        totalItemsQty={preOrderItemsTotalQty}
        cartTotal={
          <PreOrderTotal checkout={preOrderCheckout!} isAttendeeTotal />
        }
        footerButtons={[
          <Button
            key="0"
            onClick={
              isHost
                ? handleOnManagePreOrderClick
                : handleOnConfirmPreOrderClick
            }
            loading={checkingOutOfStockItems || isConfirmPending}
            disabled={isAttendee && preOrderItems.length === 0}
          >
            {isHost
              ? t('preOrder.managePreOrder')
              : t('preOrder.confirmYourOrder')}
          </Button>,
          <Button key="1" variant="secondary" onClick={handleLeavePreOrder}>
            {t('preOrder.leavePreOrder')}
          </Button>,
        ]}
      />
      {isAttendee && <PreOrderHostInfoModal />}
      <CartOutOfStockPopup onContinue={handleContinueOutOfStockPopup} />
      <LeavePreOrderModal />
    </div>
  )
}
